import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface BlogEntry {
    id: number;
    title: string;
    slug: string;
    visible: boolean;
    publishDate: string;
}

interface Props {
    onEdit: (entry: BlogEntry) => void;
}

const formatDate = (iso: string) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
};

const BlogTable: React.FC<Props> = ({ onEdit }) => {
    const [entries, setEntries]       = useState<BlogEntry[]>([]);
    const [confirmId, setConfirmId]   = useState<number | null>(null);

    const load = async () => {
        const { data } = await axios.get<BlogEntry[]>('/api/blog');
        setEntries(data);
    };

    useEffect(() => { load(); }, []);

    const handleDelete = async (id: number) => {
        await axios.delete(`/api/blog/${id}`);
        setEntries(prev => prev.filter(e => e.id !== id));
        setConfirmId(null);
    };

    return (
        <>
            <div className="bg-surface rounded border border-border overflow-hidden">
                <table className="min-w-full text-sm">
                    <thead className="bg-paper font-label text-sm text-muted">
                    <tr>
                        <th className="px-4 py-3 text-left">Title</th>
                        <th className="px-4 py-3 text-left">Slug</th>
                        <th className="px-4 py-3 text-left">Published</th>
                        <th className="px-4 py-3 text-left">Visible</th>
                        <th className="px-4 py-3 text-left">Actions</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                    {entries.length === 0 && (
                        <tr>
                            <td colSpan={5} className="px-4 py-6 text-center text-text-secondary">
                                No blog posts yet.
                            </td>
                        </tr>
                    )}
                    {entries.map(entry => (
                        <tr key={entry.id} className="hover:bg-background transition-colors">
                            <td className="px-4 py-3 font-medium text-on-surface">{entry.title}</td>
                            <td className="px-4 py-3 text-text-secondary font-mono text-xs">{entry.slug}</td>
                            <td className="px-4 py-3 text-text-secondary">{formatDate(entry.publishDate)}</td>
                            <td className="px-4 py-3">
                                <span className={`font-label text-sm font-medium ${entry.visible ? 'text-forest' : 'text-muted'}`}>
                                    {entry.visible ? 'Published' : 'Draft'}
                                </span>
                            </td>
                            <td className="px-4 py-3 flex gap-2">
                                <button
                                    onClick={() => onEdit(entry)}
                                    className="btn-secondary btn-sm"
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => setConfirmId(entry.id)}
                                    className="btn-danger btn-sm"
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Delete confirmation */}
            {confirmId !== null && (
                <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50">
                    <div className="bg-surface border border-border rounded p-6 w-full max-w-sm shadow-md">
                        <h3 className="text-lg font-semibold text-on-surface mb-2">Delete Post</h3>
                        <p className="text-text-secondary mb-6">
                            Are you sure you want to delete <strong className="text-on-surface">
                                {entries.find(e => e.id === confirmId)?.title}
                            </strong>? This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setConfirmId(null)}
                                className="px-4 py-2 rounded border border-border text-on-surface hover:bg-background transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleDelete(confirmId)}
                                className="btn-danger"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BlogTable;
